import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, UsersThree, PencilSimple, Eye, EyeSlash, Key, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import SidebarThemePicker from '@/components/SidebarThemePicker';
import StaffProfileDialog from '@/components/StaffProfileDialog';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

export default function StaffPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [revealedFor, setRevealedFor] = useState(null); // { id, password }
  const [collectionFor, setCollectionFor] = useState(null); // user
  const [collectionData, setCollectionData] = useState([]);

  const fetchUsers = () => {
    api.get('/users').then(r => setUsers(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchUsers(); }, []);

  // Shared save handler for both Add and Edit. The dialog passes back the
  // full payload (form state with monthly_salary already coerced to number).
  const handleProfileSave = async (payload, isEdit) => {
    if (isEdit) {
      // PUT — only fields with explicit values flow through (the dialog
      // strips blank password). Backend ignores unset/None entries.
      await api.put(`/users/${editTarget.id}`, payload);
      toast.success('Staff updated');
    } else {
      // Create via auth/register, then PATCH the extended profile fields.
      const { data: created } = await api.post('/auth/register', {
        name: payload.name, email: payload.email, password: payload.password, role: payload.role,
      });
      const newId = created?.id || created?._id;
      if (newId) {
        const extras = { ...payload };
        delete extras.name; delete extras.email; delete extras.password; delete extras.role;
        if (Object.values(extras).some(v => v !== '' && v !== null && v !== undefined && v !== 0)) {
          await api.put(`/users/${newId}`, extras).catch(() => {});
        }
      }
      toast.success('Staff member added');
    }
    fetchUsers();
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? `${u.name} hidden` : `${u.name} restored`);
      fetchUsers();
    } catch { toast.error('Failed to update'); }
  };

  // Admin override: set the sidebar theme for any user. The change takes effect
  // for that user the next time their browser fetches /auth/me (refresh / new login).
  const handleThemeFor = async (target, themeId) => {
    try {
      await api.put(`/users/${target.id}`, { sidebar_bg: themeId });
      toast.success(`Sidebar theme set for ${target.name}`);
      fetchUsers();
    } catch { toast.error('Failed to update theme'); }
  };

  const revealPassword = async (u) => {
    try {
      const { data } = await api.get(`/users/${u.id}/password`);
      setRevealedFor({ id: u.id, password: data.password });
    } catch { toast.error('Cannot fetch password'); }
  };

  const openCollection = async (u) => {
    setCollectionFor(u);
    setCollectionData([]);
    try {
      const { data } = await api.get(`/users/${u.id}/collection`);
      setCollectionData(data || []);
    } catch { toast.error('Failed to load collection'); }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[#EF4444]">Admin access required</p>
      </div>
    );
  }

  const totalCollection = collectionData.reduce((s, m) => s + (m.total || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowAdd(true)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="add-staff-btn">
          <Plus size={16} className="mr-2" /> Add Staff
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-lg flex items-center justify-center">
                <UsersThree size={20} className="text-[#3B82F6]" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Total Staff</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'var(--font-heading)' }}>{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Admins</p>
            <p className="text-2xl font-bold text-[#3B82F6]" style={{ fontFamily: 'var(--font-heading)' }}>{users.filter(u => u.role === 'admin').length}</p>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
          <CardContent className="p-5">
            <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Staff Members</p>
            <p className="text-2xl font-bold text-[#10B981]" style={{ fontFamily: 'var(--font-heading)' }}>{users.filter(u => u.role === 'staff').length}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {loading ? <p className="p-4 text-sm text-[#52525B]">Loading...</p> : users.length === 0 ? (
            <p className="p-8 text-center text-sm text-[#52525B]">No staff members</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Designation</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Email</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Mobile</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Role</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Salary</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Status</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Joined</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map(u => (
                  <TableRow key={u.id} className={`border-[#E4E4E7] ${u.active === false ? 'opacity-50' : ''}`}>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-2.5">
                        {u.photo ? (
                          <img src={u.photo} alt={u.name} className="w-7 h-7 rounded-full object-cover" />
                        ) : (
                          <div className="w-7 h-7 bg-[#3B82F6] rounded-full flex items-center justify-center text-white text-[10px] font-bold">{(u.name || '?').charAt(0)}</div>
                        )}
                        <span className="font-medium">{u.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-xs text-[#52525B]">{u.designation || <span className="text-[#A1A1AA]">—</span>}</TableCell>
                    <TableCell className="text-sm text-[#52525B]">{u.email}</TableCell>
                    <TableCell className="text-xs text-[#52525B]">{u.mobile || <span className="text-[#A1A1AA]">—</span>}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-md text-[10px] capitalize ${u.role === 'admin' ? 'bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20' : 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20'}`} variant="outline">
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono">{u.monthly_salary > 0 ? fmt(u.monthly_salary) : <span className="text-[#A1A1AA]">—</span>}</TableCell>
                    <TableCell>
                      {u.active === false ? (
                        <Badge className="rounded-md text-[10px] bg-[#A1A1AA]/10 text-[#52525B] border-[#A1A1AA]/30" variant="outline">Hidden</Badge>
                      ) : (
                        <Badge className="rounded-md text-[10px] bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20" variant="outline">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#52525B]">{u.joining_date ? new Date(u.joining_date).toLocaleDateString('en-IN') : (u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN') : '-')}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => openCollection(u)} data-testid={`collection-${u.id}`} title="Collection History">
                          <ChartBar size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditTarget(u)} data-testid={`edit-staff-${u.id}`} title="Edit">
                          <PencilSimple size={14} />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#F59E0B]" onClick={() => revealPassword(u)} data-testid={`reveal-pw-${u.id}`} title="Show password">
                          <Key size={14} />
                        </Button>
                        <SidebarThemePicker
                          value={u.sidebar_bg || ''}
                          onChange={(themeId) => handleThemeFor(u, themeId)}
                          triggerSize="sm"
                          align="end"
                          testIdPrefix={`theme-${u.id}`}
                        />
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                          onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} title={u.active === false ? 'Show' : 'Hide'}>
                          {u.active === false ? <Eye size={14} /> : <EyeSlash size={14} />}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add + Edit Staff — shared sectioned profile dialog (Phase 1). */}
      <StaffProfileDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        target={null}
        managers={users}
        onSaved={handleProfileSave}
      />
      <StaffProfileDialog
        open={!!editTarget}
        onOpenChange={(v) => { if (!v) setEditTarget(null); }}
        target={editTarget}
        managers={users}
        onSaved={handleProfileSave}
      />

      {/* Reveal password */}
      <Dialog open={!!revealedFor} onOpenChange={(v) => { if (!v) setRevealedFor(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7]">
          <DialogHeader>
            <DialogTitle>Password</DialogTitle>
            <DialogDescription>Plain password is shown only for accounts created/updated by an admin.</DialogDescription>
          </DialogHeader>
          <div className="bg-[#FFFBEB] border border-[#F59E0B]/30 rounded-lg p-4">
            <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B] mb-1">Password</p>
            <p className="text-xl font-mono font-bold text-[#0A0A0A]" data-testid="revealed-password">{revealedFor?.password || <span className="text-sm font-sans italic text-[#A1A1AA]">Not stored — please reset.</span>}</p>
          </div>
          <DialogFooter>
            <Button onClick={() => setRevealedFor(null)} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collection History */}
      <Dialog open={!!collectionFor} onOpenChange={(v) => { if (!v) { setCollectionFor(null); setCollectionData([]); } }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-3xl">
          <DialogHeader>
            <DialogTitle>{collectionFor?.name} — Collection History</DialogTitle>
            <DialogDescription>Month-wise breakdown of payments collected against invoices created by this staff.</DialogDescription>
          </DialogHeader>
          {collectionData.length === 0 ? (
            <p className="text-sm text-[#52525B] text-center py-6">No collection data yet.</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Month</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Invoices</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Service Charge</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Govt Fee</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collectionData.map((m, idx) => (
                    <TableRow key={m.month} className={idx % 2 === 1 ? 'bg-[#F9FAFB]' : ''} data-testid={`collection-row-${m.month}`}>
                      <TableCell className="text-sm font-semibold">{m.label}</TableCell>
                      <TableCell className="text-sm text-right font-mono">{m.count}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-[#10B981]">{fmt(m.service_charge)}</TableCell>
                      <TableCell className="text-sm text-right font-mono text-[#3B82F6]">{fmt(m.govt_fee)}</TableCell>
                      <TableCell className="text-sm text-right font-mono font-bold">{fmt(m.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end items-center gap-3 pt-3 border-t border-[#E4E4E7] mt-2">
                <span className="text-xs uppercase tracking-[0.12em] font-semibold text-[#52525B]">Grand Total</span>
                <span className="text-lg font-bold font-mono text-[#0A0A0A]" data-testid="collection-grand-total">{fmt(totalCollection)}</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
